import type { DesignMode } from '@/track/generate'
import { SHOP_BASE_URL } from './constants'

function smoothScrollTo(element: HTMLElement | Element, targetY: number): void {
  // 确保目标元素存在
  if (!element) {
    console.error('Element not found')
    return
  }

  const currentY: number = element.scrollTop // 获取当前滚动位置
  const diffY: number = targetY - currentY // 计算差值
  let startTime: number | null = null

  // 动画函数
  function animation(currentTime: number): void {
    if (startTime === null) startTime = currentTime
    const timeElapsed: number = currentTime - startTime
    const run: number = ease(timeElapsed, currentY, diffY, 500) // 500 是动画持续时间（毫秒）
    // 进行滚动
    element.scrollTo(0, run)

    // 如果还未到达目标位置，继续请求动画帧
    if (timeElapsed < 500) {
      window.requestAnimationFrame(animation)
    } else {
      element.scrollTo(0, targetY) // 最终定位到目标位置
    }
  }

  // 缓动函数
  function ease(t: number, b: number, c: number, d: number): number {
    t /= d / 2
    if (t < 1) return (c / 2) * t + b
    t--
    return (-c / 2) * (t * (t - 2) - 1) + b
  }

  // 启动动画
  window.requestAnimationFrame(animation)
}

export const scrollToBottom = (query: string) => {
  const contentBox = document.querySelector(`${query}`)
  if (!contentBox) return
  smoothScrollTo(contentBox, contentBox.scrollHeight - contentBox.clientHeight)
  // contentBox.scrollTop =
  //     contentBox.scrollHeight - contentBox.clientHeight;
}

export const copyText = async (val: string, call?: () => void): Promise<void> => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(val)
      // console.log('已成功复制')
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = val
      textarea.style.position = 'fixed'
      textarea.style.top = '-9999px'
      textarea.style.left = '-9999px'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      textarea.focus()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      // console.log('已手动复制')
    }
    call && call()
  } catch (err) {
    console.error('复制失败:', err)
    // console.log('使用window.prompt复制')
    // 兼容性处理
    window.prompt('复制到剪贴板：', val)
    throw err // 可以选择抛出错误，以便调用者处理
  }
}

// 复制图片
export const copyImage = async (imageUrl: string, isBase64: boolean = false): Promise<void> => {
  let base64Url: string = ''
  if (!isBase64) {
    base64Url = await imageUrlToBase64(imageUrl)
  } else {
    base64Url = imageUrl
  }
  const parsedBase64 = parseBase64(base64Url)
  const type: string = parsedBase64.type

  // 将 base64 转为 Blob 类型
  const bytes: string = atob(parsedBase64.data)
  const ab: ArrayBuffer = new ArrayBuffer(bytes.length)
  const ua: Uint8Array = new Uint8Array(ab)
  for (let i = 0; i < bytes.length; i++) {
    ua[i] = bytes.charCodeAt(i)
  }
  const blob: Blob = new Blob([ab], { type })

  // 检查 ClipboardItem 是否可用
  //** 注意！ClipboardItem只有在https下才生效，已踩坑 */
  if ('ClipboardItem' in window) {
    await navigator.clipboard.write([new ClipboardItem({ [type]: blob })])
    // console.log('Image copied to clipboard')
  } else {
    console.error('ClipboardItem is not supported in this browser.')
  }
}

// 转换 base64
export function parseBase64(base64: string): {
  type: string
  ext: string
  data: string
} {
  const re: RegExp = new RegExp('data:(?<type>.*?);base64,(?<data>.*)')
  const res: RegExpExecArray | null = re.exec(base64)
  if (res && res.groups) {
    return {
      type: res.groups.type,
      ext: res.groups.type.split('/').slice(-1)[0],
      data: res.groups.data
    }
  } else {
    throw new Error('Invalid base64 format')
  }
}

// 图片转 base64
export const imageUrlToBase64 = (imageUrl: string): Promise<string> => {
  return new Promise((resolve: (value: string) => void, reject: (reason?: any) => void) => {
    const image: HTMLImageElement = new Image()
    image.setAttribute('crossOrigin', 'Anonymous')
    image.src = imageUrl
    image.onload = function (): void {
      const canvas: HTMLCanvasElement = document.createElement('canvas')
      canvas.width = image.width
      canvas.height = image.height
      const context: CanvasRenderingContext2D | null = canvas.getContext('2d')
      if (context) {
        context.drawImage(image, 0, 0, image.width, image.height)
        const base64Str: string = canvas.toDataURL('image/png')
        resolve(base64Str)
      } else {
        reject(new Error('Canvas context is null'))
      }
    }
    image.onerror = function (e: Event | string): void {
      reject(e)
    }
  })
}

//将 DataURL 转换为 File 对象
export const dataUrlToFile = (dataUrl: string, fileName: string = 'img.jpg'): File => {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream'
  const byteString = atob(arr[1])
  const byteArray = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i)
  }
  return new File([byteArray], fileName, { type: mime })
}

// 是否超过7天
export const isOver7Days = (firstTime: string) => {
  if (firstTime === 'undefined') return false
  const nowTime = Date.now()
  const firstTimeNum = Number(firstTime)
  const duration = 7 * 24 * 60 * 60 * 1000
  // const duration = 60 * 1000
  return nowTime >= firstTimeNum && nowTime <= firstTimeNum + duration
}

// 埋点工具方法，获取当前的设计模式
export const getDesignMode = (type: number): DesignMode => {
  if (type === 0 || type === 1) {
    return 'image'
  } else if (type === 2) {
    return 'inpainting'
  } else if (type === 3) {
    return 'HD'
  } else {
    return '3D'
  }
}

// 获取当前页面名称
export const getCurrentPageName = (isSelf: boolean = true) => {
  const pageName = localStorage.getItem('currentPageName') || ''
  if (pageName === 'challengeDetail' || pageName === 'challengeDesign') {
    return 'challenge_page'
  } else if (pageName === 'home' || pageName === 'feeds') {
    return localStorage.getItem('homePageName') || 'homepage_hot'
  } else if (
    pageName === 'generator' ||
    pageName === 'generatorImage' ||
    pageName === 'generatorInpainting' ||
    pageName === 'generator3D' ||
    pageName === 'generatorHD' ||
    pageName === 'generatorVideo' ||
    pageName === 'generatorImg2Img'
  ) {
    return 'generator_page'
  } else if (pageName === 'account') {
    return isSelf ? 'personal_homepage' : 'others_homepage'
  } else if (pageName === 'customerAccount') {
    return 'others_homepage'
  } else if (pageName === 'contentDetail') {
    return 'post_detail'
  } else if (pageName === 'myHistory') {
    return 'personal_history_page'
  } else {
    return ''
  }
}

export const getNewTagVisible = (storeKey: string, newVersion: string) => {
  const str = localStorage.getItem(storeKey)
  let obj: any = {}
  if (str?.includes('{')) {
    obj = JSON.parse(str)
  }
  if (obj.version !== newVersion) {
    localStorage.setItem(storeKey, JSON.stringify({ ...obj, version: newVersion }))
    return true
  } else {
    return isOver7Days(obj.time)
  }
}

// 跳转电商相关页面
export const goShopPages = (path: string) => {
  window.location.href = `${SHOP_BASE_URL}${path}`
}

// 视频时间格式化
export const formatDuration = (seconds: number): string => {
  if (isNaN(seconds) || seconds < 0) return '00:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) {
    return [h, m, s].map(num => String(num).padStart(2, '0')).join(':')
  } else {
    return [m, s].map(num => String(num).padStart(2, '0')).join(':')
  }
}

// 生成16位随机数
export const getRandom16Short = () => {
  return Array.from({ length: 16 }, () =>
    '123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(
      Math.floor(Math.random() * 61)
    )
  ).join('')
}
